import { UiHeroColor, UiFieldType } from '@afs/design-system'
import type {
  UiHeroPromotion,
  UiHeroProps,
  UiHeroVisuals,
} from '@afs/design-system/components/organisms/UiHero/interface.d.ts'
import type { UiFieldProps } from '@afs/design-system/components/molecules/UiField/interface.d.ts'
import type { UiButtonProps } from '@afs/design-system/components/atoms/UiButton/interface.d.ts'
import type { UiAnnonceHero } from '@afs/design-system/components/organisms/UiAnnonceHero/interface.d.ts'

import type { ProductType } from '~/extensions/product/product.type'
import type { ProductHubType } from '~/extensions/productHub/productHub.type'
import type { HomepageType } from '~/extensions/homepage/homepage.type'
import type { FaqType } from '~/extensions/faq/faq.type'
import type { ContactType } from '~/extensions/contact/contact.type'
import type { AdviceType } from '~/extensions/advice/advice.type'
import type { SubProductType } from '~/extensions/subProduct/subProduct.type'
import type { PageType } from '~/extensions/page/page.type'
import type { SimulationProposalType } from '~/extensions/simulator/simulator.type'
import type { AnnoceType } from '~/extensions/annonce/annonce.type'

import { richTextConvertorToHtml } from '../richText/richText.helper'
import { mapImage } from '../images/image.mapper'
import { mapLink } from '../link/link.mapper'
import { formatNumberToLocale } from '@/composables/useFormatNumberToLocale'
import type { SimulationExampleType } from '../loanExampleBlock/simulationExample.type'
import type { InsurancePage } from '@/extensions/insurance/insurance.type'
import type { MyselfPage } from '@/extensions/myself/myself.type'

export function mapRefreshedHeroExample(
  data: SimulationExampleType,
  simulationResponse?: SimulationProposalType,
): string {
  let loanExample = ''
  if (simulationResponse) {
    const replacements = {
      monthlyPaymentNoInsurance: `${formatNumberToLocale(simulationResponse.monthlyPaymentNoInsurance)} €/mois `,
      numberOfPayments: formatNumberToLocale(simulationResponse.numberOfPayments),
      loanAmount: `${formatNumberToLocale(simulationResponse.loanAmount)} €`,
      annualPercentageRate: formatNumberToLocale(simulationResponse.annualPercentageRate),
      totalAmountNoInsurance: formatNumberToLocale(simulationResponse.totalAmountNoInsurance),
    }
    loanExample = richTextConvertorToHtml(data.text, replacements)
  }
  return loanExample
}

export function mapheroWithSimulation(
  data: ProductType | ProductHubType | SubProductType,
  color = UiHeroColor.RED,
  simulationResponse?: SimulationProposalType,
): UiHeroProps {
  let loanExample
  if (simulationResponse && data.fields.loan_example_block) {
    const replacements = {
      monthlyPaymentNoInsurance: `${formatNumberToLocale(simulationResponse.monthlyPaymentNoInsurance)} €/mois `,
      numberOfPayments: formatNumberToLocale(simulationResponse.numberOfPayments),
      loanAmount: `${formatNumberToLocale(simulationResponse.loanAmount)} €`,
      annualPercentageRate: formatNumberToLocale(simulationResponse.annualPercentageRate),
      totalAmountNoInsurance: formatNumberToLocale(simulationResponse.totalAmountNoInsurance),
    }

    loanExample = richTextConvertorToHtml(data.fields.loan_example_block.fields.text, replacements)
  }

  return {
    title: data.fields.title,
    subtitle: data.fields.description_text,
    color,
    simulator: mapHeroSimulator(data),
    visuals: mapVisualsWithExampleBlock(data),
    loanExample,
  }
}

export function mapHeroWithPromotion(data: HomepageType): UiHeroProps {
  return {
    title: data.fields.title,
    surtitle: data.fields.on_title,
    promotion: mapHeroPromotion(data),
    simulator: mapHeroSimulator(data),
  }
}

export function maphero(data: AdviceType | ContactType | FaqType, color: UiHeroColor): UiHeroProps {
  return {
    title: data.fields.title,
    subtitle: data.fields.subtitle,
    color,
    visuals: mapVisuals(data),
  }
}

export function mapHeroSimulator(data: HomepageType | ProductType | ProductHubType | SubProductType): UiFieldProps {
  return {
    placeholder: data.fields.simulation_placeholder,
    label: data.fields.simulation_cta,
    type: UiFieldType.NUMBER,
    prefix: '€',
    required: false,
  }
}

function mapHeroPromotion(data: HomepageType): UiHeroPromotion {
  return {
    illustration1: data.fields.hero_logo_1,
    illustration2: data.fields.hero_logo_2,
    illustration3: data.fields.hero_logo_3,
    surtitle: data.fields.promo_surtitle,
    title: data.fields.promo_title,
    label: data.fields.promo_text,
    value: data.fields.promo_highlight,
    caption: data.fields.promo_subtext,
    cta: data.fields.cta_block_promo ? (mapLink(data.fields.cta_block_promo) as UiButtonProps) : undefined,
  }
}

function mapVisualsWithExampleBlock(data: ProductType | ProductHubType | SubProductType | MyselfPage): UiHeroVisuals {
  return {
    image: mapImage(data.fields.loan_example_block?.fields.picture),
    illustration1: data.fields.loan_example_block?.fields.illustration_1,
    illustration2: data.fields.loan_example_block?.fields.illustration_2,
  }
}

export function mapVisuals(data: PageType | FaqType | ContactType | AdviceType): UiHeroVisuals {
  return {
    image: mapImage(data.fields?.picture),
    illustration1: data.fields.illustration_1,
    illustration2: data.fields.illustration_2,
  }
}

export function mapAnnonceHero(data: AnnoceType): UiAnnonceHero {
  return {
    title: data.fields.title,
    subtitle: data.fields.subtitle,
    richtext: richTextConvertorToHtml(data.fields.description),
    image: mapImage(data.fields.picture),
    illustrationLeft: data.fields.illustration_left,
    illustrationRight: data.fields.illustration_rigth,
  }
}

export function mapInsuranceHero(data: InsurancePage) {
  return {
    title: data.fields.title ? data.fields.title : '',
    subtitle: data.fields.sub_title ? richTextConvertorToHtml(data.fields.sub_title) : undefined,
    visuals: {
      image: mapImage(data.fields?.picture),
      illustration1: data.fields.illustration_right,
      illustration2: data.fields.illustration_left,
    },
  }
}

export function mapMySelfHero(data: MyselfPage): UiHeroProps {
  return {
    title: data.fields.title ? data.fields.title : '',
    subtitle: data.fields.sub_title ? richTextConvertorToHtml(data.fields.sub_title) : undefined,
    visuals: mapVisualsWithExampleBlock(data),
    color: UiHeroColor.PURPLE,
    linkButtonLabel: data.fields.hero_button_label,
    loanExample: data.fields.loan_example_block
      ? richTextConvertorToHtml(data.fields.loan_example_block.fields.text)
      : '',
  }
}
